import { useState } from 'react';
import {
  Button, Space, Table, Modal, Form, Input, message, Popconfirm, Tag, Switch
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { firmsApi } from '../../../api/firms';
import type { FirmRole } from '../../../types';
import { RolePermissionsModal } from './RolePermissionsModal';

export function RolesSection({ firmId }: { firmId: number }) {
  const queryClient = useQueryClient();
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<FirmRole | null>(null);
  const [permRoleId, setPermRoleId] = useState<number | null>(null);
  const [roleForm] = Form.useForm();

  const { data: roles = [] } = useQuery({
    queryKey: ['firm-roles', firmId],
    queryFn: () => firmsApi.getRoles(firmId),
    enabled: !!firmId,
  });

  const createRoleMutation = useMutation({
    mutationFn: (data: any) => firmsApi.createRole(firmId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-roles', firmId] });
      message.success('Rol oluşturuldu');
      setIsRoleModalOpen(false);
      roleForm.resetFields();
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Rol oluşturulamadı'),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ roleId, data }: { roleId: number; data: any }) => firmsApi.updateRole(firmId, roleId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-roles', firmId] });
      message.success('Rol güncellendi');
      setIsRoleModalOpen(false);
      setEditingRole(null);
      roleForm.resetFields();
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Rol güncellenemedi'),
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (roleId: number) => firmsApi.deleteRole(firmId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firm-roles', firmId] });
      message.success('Rol silindi');
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Rol silinemedi'),
  });

  const handleRoleSubmit = (values: any) => {
    if (editingRole) {
      updateRoleMutation.mutate({ roleId: editingRole.id, data: values });
    } else {
      createRoleMutation.mutate(values);
    }
  };

  const roleColumns = [
    {
      title: 'Rol Adı',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: FirmRole) => (
        <Space>
          {name}
          {record.isDefault && <Tag color="blue">Varsayılan</Tag>}
        </Space>
      ),
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Kullanıcı Sayısı',
      dataIndex: 'userCount',
      key: 'userCount',
      render: (count: number) => <Tag icon={<TeamOutlined />}>{count}</Tag>,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: FirmRole) => (
        <Space>
          <Button type="link" onClick={() => setPermRoleId(record.id)}>İzinler</Button>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingRole(record);
              roleForm.setFieldsValue(record);
              setIsRoleModalOpen(true);
            }}
          />
          <Popconfirm
            title="Rolü silmek istediğinize emin misiniz?"
            onConfirm={() => deleteRoleMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingRole(null);
            roleForm.resetFields();
            setIsRoleModalOpen(true);
          }}
        >
          Yeni Rol
        </Button>
      </div>
      <Table columns={roleColumns} dataSource={roles} rowKey="id" />

      {permRoleId !== null && (
        <RolePermissionsModal
          key={permRoleId}
          firmId={firmId}
          roleId={permRoleId}
          open={permRoleId !== null}
          onClose={() => setPermRoleId(null)}
        />
      )}

      <Modal
        title={editingRole ? 'Rol Düzenle' : 'Yeni Rol'}
        open={isRoleModalOpen}
        onCancel={() => {
          setIsRoleModalOpen(false);
          setEditingRole(null);
          roleForm.resetFields();
        }}
        footer={null}
      >
        <Form form={roleForm} layout="vertical" onFinish={handleRoleSubmit}>
          <Form.Item
            name="name"
            label="Rol Adı"
            rules={[{ required: true, message: 'Rol adı gerekli' }]}
            extra="Rol adı, müşteri panelindeki izin yapılandırmasıyla eşleşmelidir (örn: Admin, Muhasebe, Satış)"
          >
            <Input placeholder="Örn: Admin, Muhasebe, Satış, Depo" />
          </Form.Item>

          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="isDefault" label="Varsayılan Rol" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createRoleMutation.isPending || updateRoleMutation.isPending}>
                {editingRole ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setIsRoleModalOpen(false)}>İptal</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
